import { Avatar } from "@design-system/DataDisplay/Avatar";
import { Chip } from "@design-system/Inputs/Chip";
import { Box } from "@design-system/Layout/Box";
import { FlexRow } from "@design-system/Layout/Flex";
import { Text } from "@design-system/Typography/Text";
import { cva, VariantProps } from "class-variance-authority";
import { type FC, type Ref } from "react";

export type AssignButtonSize = "sm" | "md";
export type AssignedAvatar = {
    id: string;
    label: string;
    color?: string;
    imgUrl?: string;
};

const assignButtonVariants = cva(
    "flex items-center rounded-full  border p-0.5 box-border min-w-max",
    {
        variants: {
            size: {
                sm: "h-[26px]",
                md: "h-[30px]",
            },

            active: {
                true: "border-primary",
                false: "border-tertiary",
            },

            loading: {
                true: "cursor-wait pointer-events-none",
            },
            disabled: {
                true: "bg-primary",
                false: "bg-tertiary",
            },
        },
        /*
         * loading props must be flagged with "!" as a loading button
         * is also disabled and the disabled selector is more specific
         */

        defaultVariants: {
            size: "md",
        },
    },
);

interface AssignButtonProps
    extends React.ButtonHTMLAttributes<HTMLButtonElement>,
        VariantProps<typeof assignButtonVariants> {
    size?: AssignButtonSize;
    placeholder?: string;
    disabled?: boolean;
    label?: string;
    avatars: AssignedAvatar[];
}

const Ellipsis = ({ size, n }: { size: AssignButtonSize; n: number }) => {
    return (
        <Box
            w={size === "md" ? "6" : "5"}
            h={size === "md" ? "6" : "5"}
            className="bg-secondary rounded-full flex items-center justify-center whitespace-nowrap"
        >
            <Text variant="body-sm" color="primary">
                +{n}
            </Text>
        </Box>
    );
};

const AssignButton: FC<
    AssignButtonProps & { ref?: Ref<HTMLButtonElement> }
> = ({
    active,
    loading,
    size = "md",
    placeholder,
    avatars,
    label,
    disabled,
    ref,
    ...props
}) => {
    const showPlus = !avatars?.length;
    const showAvatarEllipsis = avatars?.length > 5;
    const text =
        avatars && avatars.length > 1
            ? `${avatars.length} ${label}`
            : avatars && avatars.length === 1
              ? avatars[0].label
              : placeholder;
    return (
        <Chip
            active={active}
            {...props}
            size={size}
            disabled={disabled || (loading ?? false)}
            ref={ref}
        >
            {showPlus && <Chip.Avatar size={size} hexColor="#fff" />}
            {!!avatars?.length && (
                <FlexRow>
                    {avatars
                        ?.slice(0, showAvatarEllipsis ? 4 : avatars.length)
                        .map((user) => (
                            <Box
                                className="rounded-full border relative -mr-[7px] last:mr-0 overflow-hidden"
                                key={user.id}
                            >
                                <Avatar
                                    hexColor={user.color}
                                    imageUrl={user.imgUrl}
                                    letter={user.label[0]}
                                    size={size}
                                    variant="circle"
                                />
                            </Box>
                        ))}
                    {showAvatarEllipsis && (
                        <Box className="rounded-full border relative -mr-[7px] last:mr-0 overflow-hidden">
                            <Ellipsis size={size} n={avatars.length - 4} />
                        </Box>
                    )}
                </FlexRow>
            )}
            {text}
            <Chip.Icon name="chevronDown" />
        </Chip>
    );
};

export { AssignButton };
